import { initData } from "./telegram";

// Production: same origin as /api/*. Dev: vite proxies /api/* to uvicorn :8766.
const BASE = "";
const REQUEST_TIMEOUT_MS = 30_000;

// Dev escape hatch: open http://localhost:5173/?u=<telegram_id> in a browser.
// Backend must have DEV_AUTH=true to honour the X-Dev-User-Id header. No effect
// in prod (DEV_AUTH unset, URL never carries ?u=).
function devUserId(): string | null {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("u");
    if (fromUrl) {
      localStorage.setItem("docs.dev_user_id", fromUrl);
      return fromUrl;
    }
    return localStorage.getItem("docs.dev_user_id");
  } catch {
    return null;
  }
}

export function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { Authorization: `tma ${initData()}` };
  const dev = devUserId();
  if (dev) headers["X-Dev-User-Id"] = dev;
  return headers;
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

function describeDetail(detail: unknown): string | null {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    if (first && typeof first === "object" && "msg" in first) {
      return String((first as { msg: unknown }).msg);
    }
    return JSON.stringify(first);
  }
  if (detail && typeof detail === "object") return JSON.stringify(detail);
  return null;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = authHeaders();
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } catch (e) {
    if ((e as Error).name === "AbortError") {
      throw new ApiError(0, "Сервер не отвечает — попробуйте ещё раз");
    }
    throw new ApiError(0, "Нет соединения с сервером");
  } finally {
    clearTimeout(timeoutId);
  }

  if (!res.ok) {
    let detail: string | null = null;
    try {
      const json = await res.json();
      detail = describeDetail(json.detail);
    } catch {}
    throw new ApiError(res.status, detail ?? res.statusText ?? "Ошибка запроса");
  }
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get("content-type") ?? "";
  if (ct.includes("application/json")) return res.json() as Promise<T>;
  return res.blob() as unknown as T;
}

export const api = {
  get: <T,>(p: string) => request<T>("GET", p),
  post: <T,>(p: string, body?: unknown) => request<T>("POST", p, body),
  put: <T,>(p: string, body?: unknown) => request<T>("PUT", p, body),
  patch: <T,>(p: string, body?: unknown) => request<T>("PATCH", p, body),
  delete: <T,>(p: string) => request<T>("DELETE", p),
};

/** Upload a single file as multipart/form-data (field name "file"). */
export async function uploadFile<T>(path: string, file: File): Promise<T> {
  const fd = new FormData();
  fd.append("file", file);
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 120_000);
  let res: Response;
  try {
    // No Content-Type — the browser sets the multipart boundary.
    res = await fetch(`${BASE}${path}`, {
      method: "POST",
      headers: authHeaders(),
      body: fd,
      signal: controller.signal,
    });
  } catch (e) {
    if ((e as Error).name === "AbortError") throw new ApiError(0, "Сервер не отвечает");
    throw new ApiError(0, "Нет соединения с сервером");
  } finally {
    clearTimeout(timeoutId);
  }
  if (!res.ok) {
    let detail: string | null = null;
    try {
      const j = await res.json();
      detail = describeDetail(j.detail);
    } catch {}
    throw new ApiError(res.status, detail ?? res.statusText ?? "Ошибка загрузки");
  }
  return res.json() as Promise<T>;
}
